import { useCallback, useEffect, useState, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ItemAnalysisPDF, {
	runItemAnalysisPDF,
	buildItemAnalysisPDFEntries as buildItemAnalysisPDFFiles,
	runItemAnalysisPDFDownloads,
} from "./ItemAnalysisPDF";
import SrtExmPDF, { runSrtExmPDF, buildSrtExmPDFBlob } from "./SrtExmPDF";
import { sendFilesToInstructor, processExam } from "../../../components/Api/ExamGradingEndpoints";
import { useFacultyRank } from "../../../Hooks/useFacultyRankQuery";
import JSZip from "jszip";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import SendIcon from "@mui/icons-material/Send";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import { Alert, ButtonGroup, Divider, IconButton } from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import EmailRecipientField, {
	buildFacultyRecipientOption,
	extractRecipientEmails,
	normalizeRecipientSelection,
	parseEmailAddresses,
} from "../../../components/Utils/EmailRecipientField";
import {
	examProcessingStateAtom,
	allStudentsMatchedAtom,
	examInDatabaseAtom,
	classListFileAtom,
	currentOmrRequestAtom,
	gradeWithoutMatchingAtom,
	omrRequestsAtom,
	outputSettingsAtom,
	OUTPUT_SETTINGS_DEFAULT,
} from "../../../components/Atoms/atoms";
import {
	TextField,
	MenuItem,
	InputAdornment,
	FormControlLabel,
	Switch,
	Typography,
	Box,
} from "@mui/material";
import { computeExamMaxScore, normalizeSelectedQuestions } from "../utils/scoring";
import { getSectionGroupsFromStudents, parseRootClassIds } from "../utils/classSections";
import {
	buildExamResultsEmailDraft,
	buildExamResultsEmailHtml,
	buildExamResultsEmailInsights,
	buildExamResultsEmailText,
	inferEmailPreset,
} from "../utils/examResultsEmail";
import ReOrderDialog from "./ReOrderDialog";
import { AdvancedSettingsGradingPanel } from "./AdvancedSettingsGradingModal";

function formatFileSize(bytes) {
	if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatReturnedTimestamp(value) {
	if (!value) return "";
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return "";
	return new Intl.DateTimeFormat(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	}).format(parsed);
}

function triggerBlobDownload(blob, filename) {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
	URL.revokeObjectURL(url);
}

function getAttachmentIcon(filename) {
	const lowered = String(filename || "").toLowerCase();
	if (lowered.endsWith(".pdf")) return <PictureAsPdfOutlinedIcon color="error" />;
	if (lowered.endsWith(".csv")) return <TableViewOutlinedIcon color="success" />;
	if (lowered.endsWith(".txt")) return <ReceiptLongOutlinedIcon color="action" />;
	return <ArchiveOutlinedIcon color="action" />;
}

function getInsightColumnAlignmentClass(column) {
	if (column?.align === "center") return "text-center";
	if (column?.align === "right") return "text-right";
	return "text-left";
}

function EmailInsightsTable({ section }) {
	const columns = Array.isArray(section?.columns) ? section.columns : [];
	const rows = Array.isArray(section?.rows) ? section.rows : [];
	if (!columns.length || !rows.length) return null;

	return (
		<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
			<div className="border-b border-gray-200 bg-slate-50 px-3 py-2">
				<Typography fontWeight={600}>{section.title}</Typography>
			</div>
			<div className="overflow-x-auto">
				<table className="min-w-full border-collapse text-sm">
					<thead className="bg-slate-50">
						<tr>
							{columns.map((column) => (
								<th
									key={`${section.key}-${column.key}`}
									className={`border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600 ${getInsightColumnAlignmentClass(
										column,
									)}`}
								>
									{column.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((row, rowIndex) => (
							<tr
								key={`${section.key}-row-${rowIndex}`}
								className="odd:bg-white even:bg-slate-50/70"
							>
								{columns.map((column) => (
									<td
										key={`${section.key}-${rowIndex}-${column.key}`}
										className={`border-b border-gray-100 px-3 py-2 align-top text-sm text-slate-700 last:border-b-0 ${getInsightColumnAlignmentClass(
											column,
										)}`}
									>
										{row?.[column.key] || "N/A"}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export default function DownloadResultsDialog(props) {
	const {
		open,
		setOpen,
		token,
		reqnum,
		handleDownloadCsv,
		exportLogsOnFinish,
		getLogsBlob,
		getCsvBlobs,
		auth,
		setSnackbar,
		fetchRequestDetails,
	} = props;
	const classListFile = useAtomValue(classListFileAtom);
	const [currentOmrRequest, setCurrentOmrRequest] = useAtom(currentOmrRequestAtom);
	const [, setOmrRequests] = useAtom(omrRequestsAtom);
	const [downloadingAll, setDownloadingAll] = useState(false);
	const [zipping, setZipping] = useState(false);
	const [examProcessingState, setExamProcessingState] = useAtom(examProcessingStateAtom);
	const allStudentsMatched = useAtomValue(allStudentsMatchedAtom);
	const examInDatabase = useAtomValue(examInDatabaseAtom);
	const gradeWithoutMatching = useAtomValue(gradeWithoutMatchingAtom);
	const [sendingToInstructor, setSendingToInstructor] = useState(false);
	const access = auth?.access;
	const [filenameSuffixInput, setFilenameSuffixInput] = useState("");
	const { data: facultyRank = [], isLoading: facultyRankLoading } = useFacultyRank(token);
	const [emailPreset, setEmailPreset] = useState(() =>
		inferEmailPreset(currentOmrRequest?.delivery),
	);
	const [emailToRecipientsState, setEmailToRecipientsState] = useState([]);
	const [emailCcInput, setEmailCcInput] = useState("");
	const [emailBccInput, setEmailBccInput] = useState("opscan@ualberta.ca");
	const [emailSubject, setEmailSubject] = useState("");
	const [emailMessageBody, setEmailMessageBody] = useState("");
	const [emailSignature, setEmailSignature] = useState("");
	const [emailBodyManuallyEdited, setEmailBodyManuallyEdited] = useState(false);
	const [emailAttachments, setEmailAttachments] = useState([]);
	const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
	const [loadingEmailAttachments, setLoadingEmailAttachments] = useState(false);
	const [emailAttachmentsError, setEmailAttachmentsError] = useState("");
	const [emailInsights, setEmailInsights] = useState(null);
	const [emailIncludeInsights, setEmailIncludeInsights] = useState(false);
	const [loadingEmailInsights, setLoadingEmailInsights] = useState(false);
	const [emailInsightsError, setEmailInsightsError] = useState("");
	const [hasModifiedToInput, setHasModifiedToInput] = useState(false);
	const [activeSection, setActiveSection] = useState("downloads");
	const [emailSent, setEmailSent] = useState(false);

	const isAdmin = String(access || "").includes("ADMIN");
	const sectionNavItems = useMemo(() => {
		const items = [
			{
				id: "processing",
				title: "Processing Settings",
				description: "Scaling, question selection, and output controls.",
			},
			{
				id: "reorder",
				title: "Re-Order Workspace",
				description: "Build a normalized item analysis across versions.",
			},
			{
				id: "advanced",
				title: "Advanced Settings",
				description: "Weighted scoring, MR, omit, and per-key item controls.",
			},
			{
				id: "downloads",
				title: "Download Results",
				description: "PDF, CSV, ZIP, and matching log downloads.",
			},
		];
		if (isAdmin) {
			items.push({
				id: "email",
				title: "Send Instructor Email",
				description: "Compose, review attachments, and send results.",
			});
		}
		return items;
	}, [isAdmin]);

	const recipientList = useMemo(() => {
		const recipients = [];
		const seen = new Set();
		[currentOmrRequest?.email1, currentOmrRequest?.email2].forEach((email) => {
			const trimmed = String(email || "").trim();
			if (!trimmed) return;
			const key = trimmed.toLowerCase();
			if (seen.has(key)) return;
			seen.add(key);
			recipients.push(trimmed);
		});
		return recipients;
	}, [currentOmrRequest?.email1, currentOmrRequest?.email2]);

	const facultyRecipientOptions = useMemo(
		() => facultyRank.map((entry) => buildFacultyRecipientOption(entry)).filter(Boolean),
		[facultyRank],
	);
	const facultyRecipientsByCcid = useMemo(() => {
		const next = new Map();
		facultyRecipientOptions.forEach((recipient) => {
			if (!next.has(recipient.ccid)) {
				next.set(recipient.ccid, recipient);
			}
		});
		return next;
	}, [facultyRecipientOptions]);

	useEffect(() => {
		if (!open || hasModifiedToInput) return;
		setEmailToRecipientsState(normalizeRecipientSelection(recipientList, facultyRecipientsByCcid));
	}, [facultyRecipientsByCcid, hasModifiedToInput, open, recipientList]);

	const [outputSettings, setOutputSettings] = useAtom(outputSettingsAtom);
	const rootClassIds = useMemo(
		() => parseRootClassIds(currentOmrRequest?.class_ids),
		[currentOmrRequest?.class_ids],
	);
	const senderName = useMemo(() => {
		const firstName = String(auth?.firstName || "").trim();
		return firstName || "TSQS";
	}, [auth?.firstName]);
	const toRecipients = useMemo(
		() => extractRecipientEmails(emailToRecipientsState),
		[emailToRecipientsState],
	);
	const ccRecipients = useMemo(() => parseEmailAddresses(emailCcInput), [emailCcInput]);
	const bccRecipients = useMemo(() => parseEmailAddresses(emailBccInput), [emailBccInput]);
	const inferredEmailPreset = useMemo(
		() => inferEmailPreset(currentOmrRequest?.delivery),
		[currentOmrRequest?.delivery],
	);
	const canSplitBySection = rootClassIds.length > 1;
	const sectionGroups = useMemo(
		() =>
			getSectionGroupsFromStudents({
				students: examProcessingState?.students || [],
				classListRows: classListFile?.classlist || [],
				rootClassIds,
			}),
		[examProcessingState?.students, classListFile?.classlist, rootClassIds],
	);
	const shouldSplitBySection = Boolean(
		outputSettings?.downloadPerClassSection && canSplitBySection,
	);

	const normalizedFilenameSuffix = useMemo(() => {
		const raw = String(filenameSuffixInput || "").trim();
		if (!raw) return "";
		const cleaned = raw.replace(/[\\/:*?"<>|]+/g, "_").replace(/\s+/g, "_");
		if (!cleaned) return "";
		return cleaned.startsWith("_") ? cleaned : `_${cleaned}`;
	}, [filenameSuffixInput]);

	const appendSuffixToFilename = useCallback(
		(filename, suffix = normalizedFilenameSuffix) => {
			if (!suffix || !filename) return filename;
			const dotIndex = filename.lastIndexOf(".");
			if (dotIndex <= 0) return `${filename}${suffix}`;
			return `${filename.slice(0, dotIndex)}${suffix}${filename.slice(dotIndex)}`;
		},
		[normalizedFilenameSuffix],
	);

	const buildEmailDraftForPreset = useCallback(
		(nextPreset) =>
			buildExamResultsEmailDraft({
				presetValue: nextPreset,
				reqnum,
				instructorName: currentOmrRequest?.instructor_name,
				senderName,
			}),
		[currentOmrRequest?.instructor_name, reqnum, senderName],
	);

	const applyEmailPreset = useCallback(
		(nextPreset) => {
			const draft = buildEmailDraftForPreset(nextPreset);
			setEmailPreset(nextPreset);
			setEmailSubject(draft.subject);
			setEmailMessageBody(draft.messageBody);
			setEmailSignature(draft.signature);
			setEmailBodyManuallyEdited(false);
		},
		[buildEmailDraftForPreset],
	);

	const visibleEmailAttachments = useMemo(
		() =>
			emailAttachments.filter(
				(file) => !removedAttachmentIds.includes(String(file?.attachmentId || "")),
			),
		[emailAttachments, removedAttachmentIds],
	);
	const returnedTimestampLabel = useMemo(
		() => formatReturnedTimestamp(currentOmrRequest?.returned),
		[currentOmrRequest?.returned],
	);
	const emailInsightSections = useMemo(
		() =>
			Array.isArray(emailInsights?.sections)
				? emailInsights.sections.filter(
						(section) => Array.isArray(section?.rows) && section.rows.length > 0,
					)
				: [],
		[emailInsights],
	);
	const hasEmailInsights = emailInsightSections.length > 0;

	const finalEmailText = useMemo(
		() =>
			buildExamResultsEmailText({
				messageBody: emailMessageBody,
				signature: emailSignature,
				insights: emailInsights,
				includeInsights: emailIncludeInsights,
			}),
		[emailIncludeInsights, emailInsights, emailMessageBody, emailSignature],
	);

	const finalEmailHtml = useMemo(
		() =>
			buildExamResultsEmailHtml({
				messageBody: emailMessageBody,
				signature: emailSignature,
				subsignature: `Test Scoring & Questionnaire Services (TSQS)\nUniversity of Alberta`,
				insights: emailInsights,
				includeInsights: emailIncludeInsights,
			}),
		[emailIncludeInsights, emailInsights, emailMessageBody, emailSignature],
	);

	const handleClose = useCallback(() => {
		setOutputSettings({ ...OUTPUT_SETTINGS_DEFAULT });
		setFilenameSuffixInput("");
		setEmailToRecipientsState([]);
		setEmailCcInput("");
		setEmailBccInput("opscan@ualberta.ca");
		setEmailAttachments([]);
		setRemovedAttachmentIds([]);
		setLoadingEmailAttachments(false);
		setEmailAttachmentsError("");
		setEmailInsights(null);
		setEmailIncludeInsights(false);
		setLoadingEmailInsights(false);
		setEmailInsightsError("");
		setEmailPreset(inferredEmailPreset);
		setEmailSubject("");
		setEmailMessageBody("");
		setEmailSignature("");
		setEmailBodyManuallyEdited(false);
		setHasModifiedToInput(false);
		setEmailSent(false);
		setActiveSection("downloads");
		setOpen(false);
	}, [inferredEmailPreset, setOpen, setOutputSettings]);

	useEffect(() => {
		if (!open) {
			setFilenameSuffixInput("");
			setEmailToRecipientsState([]);
			setEmailCcInput("");
			setEmailBccInput("opscan@ualberta.ca");
			setEmailAttachments([]);
			setRemovedAttachmentIds([]);
			setLoadingEmailAttachments(false);
			setEmailAttachmentsError("");
			setEmailInsights(null);
			setEmailIncludeInsights(false);
			setLoadingEmailInsights(false);
			setEmailInsightsError("");
			setEmailBodyManuallyEdited(false);
			setHasModifiedToInput(false);
			setActiveSection("downloads");
			return;
		}

		const draft = buildExamResultsEmailDraft({
			presetValue: inferredEmailPreset,
			reqnum,
			instructorName: currentOmrRequest?.instructor_name,
			senderName,
		});
		setEmailPreset(inferredEmailPreset);
		setEmailSubject(draft.subject);
		setEmailMessageBody(draft.messageBody);
		setEmailSignature(draft.signature);
		setEmailBodyManuallyEdited(false);
		setRemovedAttachmentIds([]);
	}, [currentOmrRequest?.instructor_name, inferredEmailPreset, open, reqnum, senderName]);

	useEffect(() => {
		if (!isAdmin && activeSection === "email") {
			setActiveSection("downloads");
		}
	}, [activeSection, isAdmin]);

	// Best guess of current total score for helper text (safe fallback to # of questions)
	const questionNumbers = useMemo(() => {
		const lq = Number(examProcessingState?.header?.lqnum);
		const hq = Number(examProcessingState?.header?.hqnum);
		if (!Number.isFinite(lq) || !Number.isFinite(hq) || hq < lq) return [];
		return Array.from({ length: hq - lq + 1 }, (_, idx) => lq + idx);
	}, [examProcessingState?.header?.lqnum, examProcessingState?.header?.hqnum]);

	const selectedQuestionSet = useMemo(() => {
		const selected = normalizeSelectedQuestions(
			outputSettings?.selectedQuestions,
			examProcessingState?.header?.lqnum,
			examProcessingState?.header?.hqnum,
		);
		return selected ?? new Set(questionNumbers);
	}, [
		outputSettings?.selectedQuestions,
		questionNumbers,
		examProcessingState?.header?.lqnum,
		examProcessingState?.header?.hqnum,
	]);

	const noQuestionsSelected =
		Array.isArray(outputSettings?.selectedQuestions) && selectedQuestionSet.size === 0;
	const selectedQuestionsPayload = useMemo(() => {
		if (!Array.isArray(outputSettings?.selectedQuestions)) return null;
		return [...selectedQuestionSet].sort((left, right) => left - right);
	}, [outputSettings?.selectedQuestions, selectedQuestionSet]);

	useEffect(() => {
		if (!open || !isAdmin) return undefined;
		if (!examProcessingState?.header?.reqnum || !examInDatabase || noQuestionsSelected) {
			setEmailInsights(null);
			setLoadingEmailInsights(false);
			setEmailInsightsError("");
			return undefined;
		}

		let active = true;

		const loadEmailInsights = async () => {
			setLoadingEmailInsights(true);
			setEmailInsightsError("");
			try {
				const response = await processExam(token, examProcessingState.header.reqnum, false, {
					header: examProcessingState.header,
					answer_keys: examProcessingState.answer_keys,
					student_responses: examProcessingState.students,
					selected_questions: selectedQuestionsPayload,
				});
				if (!active) return;
				const insights = buildExamResultsEmailInsights({
					analysesByVersion: response?.analyses_by_version || [],
					students: examProcessingState?.students || [],
					pointBiserialMode: outputSettings?.tabulationPointBiserialMode || "crpb",
					selectedQuestionCount: selectedQuestionSet.size,
					totalQuestionCount: questionNumbers.length,
					gradeWithoutMatching,
					hasClassIds: rootClassIds.length > 0 || Boolean(currentOmrRequest?.class_ids),
				});
				setEmailInsights(insights);
			} catch (error) {
				if (!active) return;
				console.error("Preparing email insights failed:", error);
				setEmailInsights(null);
				setEmailInsightsError(
					error?.response?.data?.error ||
						error?.message ||
						"Unable to generate the automatic exam insights summary right now.",
				);
			} finally {
				if (active) setLoadingEmailInsights(false);
			}
		};

		loadEmailInsights();

		return () => {
			active = false;
		};
	}, [
		currentOmrRequest?.class_ids,
		examInDatabase,
		examProcessingState,
		gradeWithoutMatching,
		isAdmin,
		noQuestionsSelected,
		open,
		outputSettings?.tabulationPointBiserialMode,
		questionNumbers.length,
		rootClassIds.length,
		selectedQuestionSet.size,
		selectedQuestionsPayload,
		token,
	]);

	useEffect(() => {
		if (!open) return;
		if (emailBodyManuallyEdited) return;
		const draft = buildEmailDraftForPreset(emailPreset);
		setEmailMessageBody(draft.messageBody);
	}, [buildEmailDraftForPreset, emailBodyManuallyEdited, emailPreset, open]);

	const currentTotalScore = useMemo(() => {
		const keys = examProcessingState?.answer_keys || [];
		const maxScore = computeExamMaxScore(
			keys,
			null,
			normalizeSelectedQuestions(
				outputSettings?.selectedQuestions,
				examProcessingState?.header?.lqnum,
				examProcessingState?.header?.hqnum,
			),
		);
		return maxScore > 0 ? Number(maxScore.toFixed(3)) : 0;
	}, [examProcessingState, outputSettings?.selectedQuestions]);

	const scalingError =
		outputSettings.scalingEnabled &&
		(outputSettings.scalingTarget === "" ||
			isNaN(Number(outputSettings.scalingTarget)) ||
			Number(outputSettings.scalingTarget) <= 0);
	const bonusMarksError =
		outputSettings.bonusMarks !== "" &&
		(isNaN(Number(outputSettings.bonusMarks)) || Number(outputSettings.bonusMarks) < 0);

	const disabledCommon =
		!examProcessingState ||
		examProcessingState.length === 0 ||
		reqnum !== examProcessingState.header.reqnum ||
		!examInDatabase ||
		!allStudentsMatched ||
		noQuestionsSelected;
	const emailSendDisabled =
		disabledCommon ||
		(emailIncludeInsights && loadingEmailInsights) ||
		loadingEmailAttachments ||
		visibleEmailAttachments.length === 0 ||
		toRecipients.length === 0 ||
		!emailSubject.trim() ||
		!finalEmailText.trim();

	const getFilenameSuffixSlotProps = () => ({
		input: {
			endAdornment: filenameSuffixInput ? (
				<InputAdornment position="end">
					<Button size="small" type="button" onClick={() => setFilenameSuffixInput("")}>
						Clear
					</Button>
				</InputAdornment>
			) : null,
		},
	});

	const handleSelectAllQuestions = useCallback(() => {
		setOutputSettings((s) => ({ ...s, selectedQuestions: null }));
	}, [setOutputSettings]);

	const handleClearAllQuestions = useCallback(() => {
		setOutputSettings((s) => ({ ...s, selectedQuestions: [] }));
	}, [setOutputSettings]);

	const toggleQuestionSelection = useCallback(
		(questionNo) => {
			const hasExplicitSelection = Array.isArray(outputSettings?.selectedQuestions);
			const current = hasExplicitSelection
				? normalizeSelectedQuestions(
						outputSettings.selectedQuestions,
						examProcessingState?.header?.lqnum,
						examProcessingState?.header?.hqnum,
					) || new Set()
				: new Set(questionNumbers);
			if (current.has(questionNo)) current.delete(questionNo);
			else current.add(questionNo);
			const next = questionNumbers.filter((q) => current.has(q));
			setOutputSettings((s) => ({ ...s, selectedQuestions: next }));
		},
		[
			outputSettings?.selectedQuestions,
			examProcessingState?.header?.lqnum,
			examProcessingState?.header?.hqnum,
			questionNumbers,
			setOutputSettings,
		],
	);

	// Helper: save a blob either via File Save Picker (Chromium) or regular download fallback
	async function saveBlob(blob, filename, description = "File") {
		if (window.showSaveFilePicker) {
			const handle = await window.showSaveFilePicker({
				suggestedName: filename,
				types: [{ description, accept: { "*/*": [`.${filename.split(".").pop()}`] } }],
			});
			const writable = await handle.createWritable();
			await writable.write(blob);
			await writable.close();
			return;
		}
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = filename;
		a.click();
		URL.revokeObjectURL(url);
	}

	const buildTabulationPdfEntries = useCallback(async () => {
		if (shouldSplitBySection && sectionGroups.length > 0) {
			return Promise.all(
				sectionGroups.map(async ({ classId, students }) => {
					const { blob, filename } = await buildSrtExmPDFBlob({
						examProcessingState,
						token,
						outputSettings,
						sectionClassId: classId,
						sectionStudentRspIds: students.map((student) => student.rsp_id),
					});
					return { name: filename, blob };
				}),
			);
		}

		const { blob, filename } = await buildSrtExmPDFBlob({
			examProcessingState,
			token,
			outputSettings,
		});
		return [{ name: filename, blob }];
	}, [examProcessingState, token, outputSettings, sectionGroups, shouldSplitBySection]);

	const buildItemAnalysisPdfEntries = useCallback(async () => {
		if (shouldSplitBySection && sectionGroups.length > 0) {
			const entries = [];
			for (const { classId, students } of sectionGroups) {
				const sectionEntries = await buildItemAnalysisPDFFiles({
					examProcessingState,
					token,
					outputSettings,
					sectionClassId: classId,
					sectionStudentRspIds: students.map((student) => student.rsp_id),
				});
				entries.push(
					...sectionEntries.map(({ filename, blob }) => ({
						name: filename,
						blob,
					})),
				);
			}
			return entries;
		}

		const entries = await buildItemAnalysisPDFFiles({
			examProcessingState,
			token,
			outputSettings,
		});
		return entries.map(({ filename, blob }) => ({ name: filename, blob }));
	}, [examProcessingState, token, outputSettings, sectionGroups, shouldSplitBySection]);

	const downloadTabulationPdfs = useCallback(
		async ({ filenameSuffix = "" } = {}) => {
			if (shouldSplitBySection && sectionGroups.length > 0) {
				for (const { classId, students } of sectionGroups) {
					await runSrtExmPDF({
						examProcessingState,
						token,
						outputSettings,
						filenameSuffix,
						sectionClassId: classId,
						sectionStudentRspIds: students.map((student) => student.rsp_id),
					});
				}
				return;
			}

			await runSrtExmPDF({
				examProcessingState,
				token,
				outputSettings,
				filenameSuffix,
			});
		},
		[examProcessingState, token, outputSettings, sectionGroups, shouldSplitBySection],
	);

	const downloadItemAnalysisPdfs = useCallback(
		async ({ filenameSuffix = "" } = {}) => {
			if (shouldSplitBySection && sectionGroups.length > 0) {
				for (const { classId, students } of sectionGroups) {
					await runItemAnalysisPDFDownloads({
						examProcessingState,
						token,
						outputSettings,
						filenameSuffix,
						sectionClassId: classId,
						sectionStudentRspIds: students.map((student) => student.rsp_id),
					});
				}
				return;
			}

			await runItemAnalysisPDFDownloads({
				examProcessingState,
				token,
				outputSettings,
				filenameSuffix,
			});
		},
		[examProcessingState, token, outputSettings, sectionGroups, shouldSplitBySection],
	);

	const gatherAllAsBlobs = useCallback(
		async ({ applyFilenameSuffix = false } = {}) => {
			// 1) PDFs
			const [itemEntries, statsEntries] = await Promise.all([
				buildItemAnalysisPdfEntries(),
				buildTabulationPdfEntries(),
			]);

			// 2) CSVs
			let csvEntries = [];
			if (typeof getCsvBlobs === "function") {
				csvEntries = await getCsvBlobs({
					examProcessingState,
					reqnum,
					outputSettings,
				});
			} else {
				// fallback: call your existing handler if it returns a blob, else skip
				// e.g., const b = await downloadCsvAsBlob(); csvEntries = [{ name: `Results_${reqnum}.csv`, blob: b }];
			}

			// 3) Matching log
			let logEntry = null;
			if (typeof getLogsBlob === "function") {
				logEntry = await getLogsBlob({
					examProcessingState,
					classListFile: classListFile.classlist,
				});
			} else {
				// fallback: if exportLogsOnFinish triggers a download, you’d need a blob variant for zipping.
			}

			let files = [...itemEntries, ...statsEntries, ...csvEntries];
			if (logEntry?.blob) files.push(logEntry);
			if (applyFilenameSuffix) {
				files = files.map((f) => ({
					...f,
					originalName: f?.originalName || f?.name,
					name: appendSuffixToFilename(f?.name),
				}));
			}

			return files;
		},
		[
			appendSuffixToFilename,
			buildItemAnalysisPdfEntries,
			buildTabulationPdfEntries,
			classListFile,
			examProcessingState,
			getCsvBlobs,
			getLogsBlob,
			outputSettings,
			reqnum,
		],
	);

	const mapFilesToEmailAttachments = useCallback((files) => {
		return files.map((file) => {
			const identityName = file?.originalName || file?.name || "attachment";
			return {
				...file,
				attachmentId: `${identityName}::${file?.blob?.size || 0}`,
				size: file?.blob?.size || 0,
				contentType: file?.blob?.type || "application/octet-stream",
			};
		});
	}, []);

	const refreshEmailAttachments = useCallback(async () => {
		if (!open || !isAdmin) return;
		setLoadingEmailAttachments(true);
		setEmailAttachmentsError("");
		try {
			const files = await gatherAllAsBlobs({ applyFilenameSuffix: true });
			setEmailAttachments(mapFilesToEmailAttachments(files));
		} catch (error) {
			console.error("Preparing email attachments failed:", error);
			setEmailAttachmentsError(
				error?.message || "Unable to prepare attachments for the email composer.",
			);
			setEmailAttachments([]);
		} finally {
			setLoadingEmailAttachments(false);
		}
	}, [gatherAllAsBlobs, isAdmin, mapFilesToEmailAttachments, open]);

	useEffect(() => {
		if (!open || !isAdmin) return undefined;
		let active = true;

		const loadAttachments = async () => {
			setLoadingEmailAttachments(true);
			setEmailAttachmentsError("");
			try {
				const files = await gatherAllAsBlobs({ applyFilenameSuffix: true });
				if (!active) return;
				setEmailAttachments(mapFilesToEmailAttachments(files));
			} catch (error) {
				if (!active) return;
				console.error("Preparing email attachments failed:", error);
				setEmailAttachmentsError(
					error?.message || "Unable to prepare attachments for the email composer.",
				);
				setEmailAttachments([]);
			} finally {
				if (active) setLoadingEmailAttachments(false);
			}
		};

		loadAttachments();

		return () => {
			active = false;
		};
	}, [gatherAllAsBlobs, isAdmin, mapFilesToEmailAttachments, open]);

	const handleDownloadAll = async () => {
		if (disabledCommon) return;
		setDownloadingAll(true);
		try {
			await downloadItemAnalysisPdfs({ filenameSuffix: normalizedFilenameSuffix });
			await downloadTabulationPdfs({ filenameSuffix: normalizedFilenameSuffix });

			const maybeCsv = handleDownloadCsv?.({
				filenameSuffix: normalizedFilenameSuffix,
				outputSettings,
			});
			if (maybeCsv?.then) await maybeCsv;

			const maybeLog = exportLogsOnFinish?.({
				examProcessingState,
				classListFile,
				filenameSuffix: normalizedFilenameSuffix,
			});
			if (maybeLog?.then) await maybeLog;
		} catch (e) {
			console.error("Download all failed:", e);
		} finally {
			setDownloadingAll(false);
		}
	};

	const handleDownloadAllAsZip = async () => {
		if (disabledCommon) return;
		setZipping(true);
		try {
			const files = await gatherAllAsBlobs({ applyFilenameSuffix: true });
			const zip = new JSZip();
			for (const f of files) {
				if (!f?.blob || !f?.name) continue;
				zip.file(f.name, f.blob);
			}
			const zipBlob = await zip.generateAsync({ type: "blob" });
			await saveBlob(zipBlob, appendSuffixToFilename(`exam_${reqnum}_bundle.zip`), "ZIP Archive");
		} catch (e) {
			console.error("Zip download failed:", e);
		} finally {
			setZipping(false);
		}
	};

	const DownloadButtonLabel = (icon, label, filetype) => {
		return (
			<div className="flex gap-2 items-center justify-start">
				{icon}
				<div className="flex flex-col items-start">
					<p>{label}</p>
					<p className="text-sm text-gray-600">{filetype}</p>
				</div>
			</div>
		);
	};

	const handleSendToInstructor = async () => {
		if (emailSendDisabled) return;
		setSendingToInstructor(true);
		try {
			const response = await sendFilesToInstructor({
				files: visibleEmailAttachments,
				examProcessingState,
				token,
				subject: emailSubject.trim(),
				textBody: finalEmailText,
				htmlBody: finalEmailHtml,
				templatePreset: emailPreset,
				to: toRecipients,
				cc: ccRecipients,
				bcc: bccRecipients,
			});
			setSnackbar?.({
				open: true,
				severity: "success",
				message: `Email sent to ${response?.to?.join(", ") || toRecipients.join(", ")}.`,
			});
			if (response?.exam_log_warning) {
				setSnackbar?.({
					open: true,
					severity: "success",
					message: `Email sent, but the exam GDrive spreadsheet was not updated: ${response.exam_log_warning}`,
				});
			}
			const persistedReturned =
				response?.returned || currentOmrRequest?.returned || new Date().toISOString();
			const persistedStatus = response?.status || currentOmrRequest?.status || "Sent";
			setCurrentOmrRequest((current) =>
				current
					? {
							...current,
							status: persistedStatus,
							returned: persistedReturned,
						}
					: current,
			);
			setOmrRequests((currentRequests) =>
				currentRequests.map((requestItem) =>
					String(requestItem?.reqnum) === String(reqnum)
						? {
								...requestItem,
								status: persistedStatus,
								returned: persistedReturned,
							}
						: requestItem,
				),
			);
			setEmailSent(true);
			try {
				await fetchRequestDetails?.(reqnum);
			} catch (refreshError) {
				console.error("Refreshing request after email send failed:", refreshError);
			}
		} catch (e) {
			console.error("Send to instructor failed:", e);
			setSnackbar?.({
				open: true,
				severity: "error",
				message:
					e?.response?.data?.error || e?.message || "Unable to send the results email right now.",
			});
		} finally {
			setSendingToInstructor(false);
		}
	};

	const handleRemoveAttachment = useCallback((attachmentId) => {
		setRemovedAttachmentIds((current) => {
			if (current.includes(attachmentId)) return current;
			return [...current, attachmentId];
		});
	}, []);

	const handleRestoreAttachments = useCallback(() => {
		setRemovedAttachmentIds([]);
	}, []);

	const handleResetToTemplate = () => {
		applyEmailPreset(emailPreset);
		setHasModifiedToInput(false);
	};

	const handleNormalizedReorderAnalysis = useCallback(
		async (oneAnalysisArray, action) => {
			const normalizedAnalysis = oneAnalysisArray?.[0]?.analysis;
			if (!normalizedAnalysis) return;

			const nextExamProcessingState = {
				...examProcessingState,
				reorder_analysis: {
					analysis: normalizedAnalysis,
					version: "R",
				},
			};
			const nextOutputSettings = {
				...outputSettings,
				reorderedItemAnalysis: true,
			};

			setExamProcessingState(nextExamProcessingState);
			setOutputSettings(nextOutputSettings);

			if (action === "download") {
				await runItemAnalysisPDF({
					examProcessingState: nextExamProcessingState,
					token,
					outputSettings: nextOutputSettings,
					filenameSuffix: normalizedFilenameSuffix,
				});
				setSnackbar?.({
					open: true,
					message: "Normalized item analysis created and downloaded.",
					severity: "success",
				});
				return;
			}

			setSnackbar?.({
				open: true,
				message: "Normalized item analysis is ready for download.",
				severity: "success",
			});
		},
		[
			examProcessingState,
			normalizedFilenameSuffix,
			outputSettings,
			setExamProcessingState,
			setOutputSettings,
			setSnackbar,
			token,
		],
	);

	return (
		<Dialog
			open={open}
			onClose={(e, r) => r === "backdropClick" && handleClose()}
			fullWidth
			maxWidth={false}
			disableEscapeKeyDown
			PaperProps={{
				sx: {
					height: "92vh",
					maxHeight: "92vh",
					width: "min(1500px, 96vw)",
					maxWidth: "96vw",
					display: "flex",
					flexDirection: "column",
				},
			}}
		>
			<div className="flex h-full min-h-0 w-full flex-col items-stretch">
				<DialogTitle>Send or Download Exam Files</DialogTitle>
				<DialogContent
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							md: "240px minmax(0, 1fr)",
						},
						gap: 2,
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						width: "100%",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: { xs: "row", md: "column" },
							gap: 1,
							pr: { md: 2 },
							pb: { xs: 1, md: 0 },
							borderRight: { md: 1 },
							borderBottom: { xs: 1, md: 0 },
							borderColor: "divider",
							overflowX: { xs: "auto", md: "hidden" },
							overflowY: { xs: "hidden", md: "auto" },
							minWidth: 0,
						}}
					>
						{sectionNavItems.map((item) => (
							<Button
								key={item.id}
								type="button"
								onClick={() => setActiveSection(item.id)}
								variant={activeSection === item.id ? "contained" : "text"}
								color={activeSection === item.id ? "primary" : "inherit"}
								sx={{
									justifyContent: "flex-start",
									alignItems: "flex-start",
									textAlign: "left",
									textTransform: "none",
									borderRadius: 2,
									px: 2,
									py: 1.5,
									minWidth: { xs: "220px", md: "100%" },
									flexShrink: 0,
								}}
							>
								<Box>
									<Typography fontWeight={600} color="inherit">
										{item.title}
									</Typography>
									<Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
										{item.description}
									</Typography>
								</Box>
							</Button>
						))}
					</Box>
					<Box sx={{ py: 1, px: { xs: 0, md: 1 } }}>
						{activeSection === "processing" && (
							<Box className="grid grid-cols-6 gap-4 px-4 pb-2 max-h-[76dvh] overflow-y-auto">
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-3">
										<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
											<div>
												<Typography fontWeight={500}>Scaling</Typography>
												<Typography variant="caption" color="text.secondary">
													Scale exported scores to a new total while keeping the current exam totals as the
													reference point.
												</Typography>
											</div>
											<FormControlLabel
												className="m-0"
												control={
													<Switch
														checked={outputSettings.scalingEnabled}
														onChange={(e) =>
															setOutputSettings((s) => ({
																...s,
																scalingEnabled: e.target.checked,
															}))
														}
													/>
												}
												label={outputSettings.scalingEnabled ? "Enabled" : "Disabled"}
											/>
										</div>
										<div className="flex flex-col gap-3 md:flex-row md:items-start">
											<TextField
												className="flex-1"
												label="Scale total to"
												type="number"
												size="small"
												disabled={!outputSettings.scalingEnabled}
												value={
													outputSettings.scalingTarget
														? outputSettings.scalingTarget
														: currentTotalScore
												}
												onChange={(e) =>
													setOutputSettings((s) => ({
														...s,
														scalingTarget: e.target.value,
													}))
												}
												slotProps={{
													input: {
														inputProps: { min: 1, step: 1 },
														endAdornment: <InputAdornment position="end">points</InputAdornment>,
													},
												}}
												error={Boolean(scalingError)}
												helperText={
													outputSettings.scalingEnabled
														? scalingError
															? "Enter a positive number."
															: currentTotalScore
																? `Old total: ${currentTotalScore}`
																: "Current total unknown"
														: "Scaling is off"
												}
											/>
											<Button
												variant="contained"
												size="small"
												onClick={() =>
													setOutputSettings((s) => ({
														...s,
														scalingEnabled: true,
														scalingTarget: "100",
													}))
												}
											>
												Scale Out of 100 (%)
											</Button>
										</div>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
										<div>
											<Typography fontWeight={500}>Bonus marks</Typography>
											<Typography variant="caption" color="text.secondary">
												Apply a flat mark increase to each exported student score.
											</Typography>
										</div>
										<TextField
											label="Bonus marks"
											type="number"
											size="small"
											autoComplete="off"
											className="w-full md:max-w-xs"
											value={outputSettings.bonusMarks ?? ""}
											onChange={(e) =>
												setOutputSettings((s) => ({
													...s,
													bonusMarks: e.target.value,
												}))
											}
											slotProps={{
												input: {
													inputProps: { min: 0, step: 0.5 },
													endAdornment: <InputAdornment position="end">marks</InputAdornment>,
												},
											}}
											error={Boolean(bonusMarksError)}
											helperText={
												bonusMarksError
													? "Enter 0 or a positive number."
													: Number(outputSettings.bonusMarks || 0) > 0
														? `Adds +${Number(outputSettings.bonusMarks)} to each exported student score`
														: "No bonus marks applied"
											}
										/>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
										<div>
											<Typography fontWeight={500}>Point-Biserial</Typography>
											<Typography variant="caption" color="text.secondary">
												Choose which point-biserial statistic to use in exam tabulation output.
											</Typography>
										</div>
										<TextField
											select
											size="small"
											className="w-full md:max-w-sm"
											value={String(outputSettings.tabulationPointBiserialMode || "crpb")}
											onChange={(e) =>
												setOutputSettings((s) => ({
													...s,
													tabulationPointBiserialMode: e.target.value,
												}))
											}
										>
											<MenuItem value="crpb">CRPB (Corrected)</MenuItem>
											<MenuItem value="legacy_crpb">Legacy CRPB (Compatibility)</MenuItem>
											<MenuItem value="rpb">RPB (Raw)</MenuItem>
										</TextField>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
										<div>
											<Typography fontWeight={500}>Results By Section</Typography>
											<Typography variant="caption" color="text.secondary">
												{canSplitBySection
													? `Generates Student Results, Item Analysis, and Exam Tabulation per root class ID (${rootClassIds.join(", ")}).`
													: "Enable this when the request has more than one root class ID."}
											</Typography>
										</div>
										<FormControlLabel
											className="m-0"
											control={
												<Switch
													checked={Boolean(outputSettings.downloadPerClassSection)}
													disabled={!canSplitBySection}
													onChange={(e) =>
														setOutputSettings((s) => ({
															...s,
															downloadPerClassSection: e.target.checked,
														}))
													}
												/>
											}
											label={outputSettings.downloadPerClassSection ? "Enabled" : "Disabled"}
										/>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
										<div>
											<Typography fontWeight={500}>Include quintile statistics</Typography>
											<Typography variant="caption" color="text.secondary">
												Add a separate quintile section to Item Analysis PDFs when the exam
												qualifies. Default is off.
											</Typography>
										</div>
										<FormControlLabel
											className="m-0"
											control={
												<Switch
													checked={Boolean(outputSettings.includeQuintileStatistics)}
													onChange={(e) =>
														setOutputSettings((s) => ({
															...s,
															includeQuintileStatistics: e.target.checked,
														}))
													}
												/>
											}
											label={outputSettings.includeQuintileStatistics ? "Enabled" : "Disabled"}
										/>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
										<div>
											<Typography fontWeight={500}>Criterion Score</Typography>
											<Typography variant="caption" color="text.secondary">
												Use the current raw scoring setup as an external criterion in Item Analysis
												PDFs. This is a PDF-layer display mode and does not change backend
												processing.
											</Typography>
										</div>
										<FormControlLabel
											className="m-0"
											control={
												<Switch
													checked={Boolean(outputSettings.criterionScoreEnabled)}
													onChange={(e) =>
														setOutputSettings((s) => ({
															...s,
															criterionScoreEnabled: e.target.checked,
														}))
													}
												/>
											}
											label={outputSettings.criterionScoreEnabled ? "Enabled" : "Disabled"}
										/>
									</div>
								</div>
								<Divider className="col-span-6" />
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
										<div>
											<Typography fontWeight={500}>Old Exam Result Format</Typography>
											<Typography variant="caption" color="text.secondary">
												Use the legacy Student Results columns and `_w.csv` filenames. For multi-key
												exams, Item Analysis downloads are split into one PDF per version using
												`REQNUM_itm_VERSION.pdf`.
											</Typography>
										</div>
										<FormControlLabel
											className="m-0"
											control={
												<Switch
													checked={Boolean(outputSettings.oldExamResultFormat)}
													onChange={(e) =>
														setOutputSettings((s) => ({
															...s,
															oldExamResultFormat: e.target.checked,
														}))
													}
												/>
											}
											label={outputSettings.oldExamResultFormat ? "Enabled" : "Disabled"}
										/>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
										<div>
											<Typography fontWeight={500}>Weight Breakdown Columns</Typography>
											<Typography variant="caption" color="text.secondary">
												Adds one column per unique question weight, such as `Weight 1`,
												`Weight 2`, and `Weight 3`, to the exported student-results spreadsheet.
												Each column shows the student&apos;s raw subtotal earned from questions in
												that weight bucket, while the existing total score column stays unchanged.
												This only applies when the exam has non-default weights.
											</Typography>
										</div>
										<FormControlLabel
											className="m-0"
											control={
												<Switch
													checked={Boolean(outputSettings.includeWeightBreakdownColumns)}
													onChange={(e) =>
														setOutputSettings((s) => ({
															...s,
															includeWeightBreakdownColumns: e.target.checked,
														}))
													}
												/>
											}
											label={outputSettings.includeWeightBreakdownColumns ? "Enabled" : "Disabled"}
										/>
									</div>
								</div>
								<div className="col-span-6 rounded-lg border border-gray-200 bg-slate-50 px-3 py-3">
									<div className="flex flex-col gap-3">
										<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
											<div>
												<Typography fontWeight={500}>Questions to include</Typography>
												<Typography variant="caption" color="text.secondary">
													Choose which question numbers appear in exports and item analysis downloads.
												</Typography>
											</div>
											<div className="flex gap-2">
												<Button size="small" variant="text" onClick={handleSelectAllQuestions}>
													All
												</Button>
												<Button size="small" variant="text" onClick={handleClearAllQuestions}>
													None
												</Button>
											</div>
										</div>
										<Box className="max-h-48 overflow-y-auto rounded-md border border-gray-300 bg-white p-2">
											<div className="flex flex-wrap gap-1">
												{questionNumbers.map((q) => {
													const selected = selectedQuestionSet.has(q);
													return (
														<Button
															key={q}
															size="small"
															variant={selected ? "contained" : "outlined"}
															onClick={() => toggleQuestionSelection(q)}
															sx={{ minWidth: 46, borderRadius: "20px", px: 1 }}
														>
															{q}
														</Button>
													);
												})}
											</div>
										</Box>
										<Typography variant="caption" color={noQuestionsSelected ? "error" : "text.secondary"}>
											{noQuestionsSelected
												? "Select at least one question to enable downloads."
												: `${selectedQuestionSet.size} of ${questionNumbers.length} questions selected.`}
										</Typography>
									</div>
								</div>
							</Box>
						)}

						{activeSection === "reorder" && (
							<Box className="h-full min-h-0 px-2 pb-2 max-h-[76dvh] overflow-y-auto">
								<ReOrderDialog
									embedded
									token={token}
									reqnum={reqnum}
									answerKeyArray={
										Array.isArray(examProcessingState?.answer_keys)
											? examProcessingState.answer_keys
											: Object.values(examProcessingState?.answer_keys || {})
									}
									onNormalized={handleNormalizedReorderAnalysis}
								/>
							</Box>
						)}

						{activeSection === "advanced" && (
							<Box className="h-full min-h-0 px-2 pb-2  max-h-[76dvh]">
								<AdvancedSettingsGradingPanel
									active={open && activeSection === "advanced"}
									embedded
									token={token}
									reqnum={reqnum}
									examProcessingState={examProcessingState}
									setSnackbar={setSnackbar}
									onSaved={fetchRequestDetails}
								/>
							</Box>
						)}

						{activeSection === "downloads" && (
							<Box className="flex flex-col gap-4 px-2 pb-2 overflow-y-auto max-h-[76dvh]">
								<div className="flex flex-col">
									<TextField
										fullWidth
										size="small"
										label="Optional filename suffix"
										placeholder="V2"
										value={filenameSuffixInput}
										onChange={(e) => setFilenameSuffixInput(e.target.value)}
										slotProps={getFilenameSuffixSlotProps()}
										helperText="Appended to all downloaded files and email attachments from this dialog (for example: _V2)."
									/>
									<div className="grid grid-cols-2 items-center py-4  gap-2">
										<Divider className="col-span-2" />
										<div className="flex w-full justify-between">
											{DownloadButtonLabel(
												<BarChartOutlinedIcon color="action" />,
												"Item Analysis",
												"PDF",
											)}
										</div>
										<ItemAnalysisPDF
											examProcessingState={examProcessingState}
											token={token}
											reqnum={reqnum}
											examInDatabase={examInDatabase}
											allStudentsMatched={allStudentsMatched}
											downloadingAll={downloadingAll}
											outputSettings={outputSettings}
											filenameSuffix={normalizedFilenameSuffix}
										/>
										<Divider className="col-span-2" />

										{DownloadButtonLabel(
											<TableViewOutlinedIcon color="action" />,
											"Student Results",
											"CSV",
										)}
										<Button
											variant="outlined"
											disabled={downloadingAll || noQuestionsSelected}
											onClick={() =>
												handleDownloadCsv({
													filenameSuffix: normalizedFilenameSuffix,
													outputSettings,
												})
											}
											sx={{ borderRadius: "30px" }}
										>
											{downloadingAll ? <CircularProgress size={24} color="inherit" /> : "Download"}
										</Button>
										<Divider className="col-span-2" />
										{DownloadButtonLabel(
											<PictureAsPdfOutlinedIcon color="action" />,
											"Exam Tabulation",
											"PDF",
										)}
										<SrtExmPDF
											examProcessingState={examProcessingState}
											token={token}
											reqnum={reqnum}
											examInDatabase={examInDatabase}
											allStudentsMatched={allStudentsMatched}
											downloadingAll={downloadingAll}
											filenameSuffix={normalizedFilenameSuffix}
										/>
										<Divider className="col-span-2" />

										{DownloadButtonLabel(
											<ReceiptLongOutlinedIcon color="action" />,
											"Class Matching Log",
											"TXT",
										)}
										<Button
											variant="outlined"
											disabled={downloadingAll}
											sx={{ borderRadius: "30px" }}
											onClick={() =>
												exportLogsOnFinish({
													examProcessingState,
													classListFile,
													filenameSuffix: normalizedFilenameSuffix,
												})
											}
										>
											{downloadingAll ? <CircularProgress size={24} color="inherit" /> : "Download"}
										</Button>
										<Divider className="col-span-2" />
										{/* Download All (separate files) */}
										<Button
											className="col-span-2"
											variant="contained"
											color="primary"
											onClick={handleDownloadAll}
											sx={{ borderRadius: "30px", py: 1 }}
											startIcon={<DownloadForOfflineOutlinedIcon />}
											disabled={disabledCommon || downloadingAll}
										>
											{downloadingAll ? (
												<CircularProgress size={24} color="inherit" />
											) : (
												"Download All Files"
											)}
										</Button>
										{/* Download All as ZIP */}
										<Button
											className="col-span-2"
											variant="outlined"
											onClick={handleDownloadAllAsZip}
											startIcon={<ArchiveOutlinedIcon />}
											disabled={disabledCommon || zipping || downloadingAll}
											sx={{ borderStyle: "dashed", borderRadius: "30px", py: 1 }}
										>
											{zipping ? <CircularProgress size={24} color="inherit" /> : "Download as ZIP"}
										</Button>
									</div>
								</div>
							</Box>
						)}

						{isAdmin && activeSection === "email" && (
							<Box className="h-full px-2 max-h-[76dvh] py-2 overflow-y-auto">
								<div className="flex flex-col gap-3">
									{returnedTimestampLabel && (
										<Alert severity="warning">
											Results were already emailed on {returnedTimestampLabel}. Sending again may
											result in duplicate emails.
										</Alert>
									)}
									{loadingEmailInsights && (
										<Alert severity="info">
											Refreshing the exam insight summary from the current scoring data.
										</Alert>
									)}
									{emailInsightsError && (
										<Alert severity="warning">
											{emailInsightsError} You can still edit the template and send manually.
										</Alert>
									)}

									<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
										<div className="md:col-span-2">
											<EmailRecipientField
												label="To"
												placeholder="ccid@ualberta.ca"
												value={emailToRecipientsState}
												onChange={(next) => {
													setHasModifiedToInput(true);
													setEmailToRecipientsState(next);
												}}
												facultyOptions={facultyRecipientOptions}
												facultyByCcid={facultyRecipientsByCcid}
												loading={facultyRankLoading}
												error={emailToRecipientsState.length === 0}
											/>
										</div>
										<TextField
											className=" bg-white"
											size="small"
											label="CC"
											slotProps={{ htmlInput: { autoComplete: "new-password" } }}
											placeholder="optional@ualberta.ca"
											value={emailCcInput}
											onChange={(e) => setEmailCcInput(e.target.value)}
										/>
										<TextField
											className=" bg-white"
											size="small"
											label="BCC"
											slotProps={{ htmlInput: { autoComplete: "email" } }}
											placeholder="optional@ualberta.ca"
											value={emailBccInput}
											onChange={(e) => setEmailBccInput(e.target.value)}
										/>

										<TextField
											className=" bg-white"
											size="small"
											label="Subject"
											value={emailSubject}
											onChange={(e) => setEmailSubject(e.target.value)}
										/>
										<ButtonGroup size="small" fullWidth color="primary" className="w-full bg-white">
											<Button
												onClick={() => applyEmailPreset("pickup")}
												variant={emailPreset === "pickup" ? "contained" : "outlined"}
											>
												Pickup
											</Button>
											<Button
												onClick={() => applyEmailPreset("campus_mail")}
												variant={emailPreset === "campus_mail" ? "contained" : "outlined"}
											>
												Campus Mail
											</Button>
											<Button
												onClick={() => applyEmailPreset("courier")}
												variant={emailPreset === "courier" ? "contained" : "outlined"}
											>
												Courier
											</Button>
										</ButtonGroup>
										<div className="md:col-span-2 relative">
											<TextField
												multiline
												size="small"
												rows={7}
												className="w-full bg-white text-sm"
												sx={{
													"& .MuiInputBase-root": {
														fontSize: 14,
													},
												}}
												label="Body"
												value={emailMessageBody}
												onChange={(e) => {
													setEmailBodyManuallyEdited(true);
													setEmailMessageBody(e.target.value);
												}}
												placeholder="Compose the email body"
												helperText="Compose the main email message here. The result set insights summary is managed separately below."
											/>
											<div className="absolute top-0 right-0">
												<Button size="small" variant="text" onClick={handleResetToTemplate}>
													Reset to template
												</Button>
											</div>
										</div>
										<div className="md:col-span-2 rounded-xl border border-gray-200 bg-slate-50 p-3">
											<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
												<div>
													<Typography fontWeight={600}>Result Set Insights</Typography>
													<Typography variant="caption" color="text.secondary">
														Add a structured summary block beneath the main message in the sent
														email.
													</Typography>
												</div>
												<FormControlLabel
													className="m-0"
													control={
														<Switch
															checked={emailIncludeInsights}
															onChange={(e) => setEmailIncludeInsights(e.target.checked)}
														/>
													}
													label={emailIncludeInsights ? "Included" : "Excluded"}
												/>
											</div>
											<div className="mt-3">
												{loadingEmailInsights ? (
													<div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-white px-3 py-3">
														<CircularProgress size={18} />
														<Typography variant="body2" color="text.secondary">
															Building the latest insights from the current scoring data.
														</Typography>
													</div>
												) : emailIncludeInsights && hasEmailInsights ? (
													<div className="grid grid-cols-1 gap-3">
														{emailInsightSections.map((section) => (
															<EmailInsightsTable key={section.key} section={section} />
														))}
													</div>
												) : emailIncludeInsights ? (
													<div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-3">
														<Typography variant="body2" color="text.secondary">
															No structured insights are available right now. The email will send
															without this block unless the summary finishes generating.
														</Typography>
													</div>
												) : (
													<div className="rounded-lg border border-dashed border-gray-200 bg-white px-3 py-3">
														<Typography variant="body2" color="text.secondary">
															Result set insights are off for this email.
														</Typography>
													</div>
												)}
											</div>
										</div>
										<TextField
											multiline
											size="small"
											className="md:col-span-2 bg-white"
											minRows={2}
											sx={{
												"& .MuiInputBase-root": {
													fontSize: 14,
												},
											}}
											label="Signature"
											value={emailSignature}
											onChange={(e) => setEmailSignature(e.target.value)}
										/>
									</div>

									<div className="rounded-xl border border-gray-200 bg-white p-3">
										<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
											<div>
												<Typography fontWeight={600}>
													Attachments to send ({visibleEmailAttachments.length})
												</Typography>
												<Typography variant="caption" color="text.secondary">
													Click a file to download and inspect it before sending.
												</Typography>
											</div>
											<div className="flex w-1/2 justify-between ">
												<TextField
													size="small"
													label="Optional filename suffix"
													value={filenameSuffixInput}
													onChange={(e) => setFilenameSuffixInput(e.target.value)}
													slotProps={getFilenameSuffixSlotProps()}
												/>
												<Button
													variant="outlined"
													className=""
													startIcon={<RefreshIcon />}
													onClick={refreshEmailAttachments}
													disabled={loadingEmailAttachments || disabledCommon}
												>
													Refresh attachments
												</Button>
											</div>
										</div>

										{loadingEmailAttachments ? (
											<div className="flex items-center gap-3 py-4">
												<CircularProgress size={20} />
												<Typography variant="body2" color="text.secondary">
													Preparing current attachments...
												</Typography>
											</div>
										) : emailAttachmentsError ? (
											<Alert severity="error" className="mt-3">
												{emailAttachmentsError}
											</Alert>
										) : visibleEmailAttachments.length === 0 ? (
											<Alert severity="warning" className="mt-3">
												No attachments are currently selected for sending.
											</Alert>
										) : (
											<div className=" grid grid-cols-3 gap-3">
												{visibleEmailAttachments.map((file) => (
													<div
														key={file.attachmentId}
														className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
													>
														<button
															type="button"
															className="flex min-w-0 flex-1 items-center gap-3 text-left hover:text-primary"
															onClick={() => triggerBlobDownload(file.blob, file.name)}
														>
															{getAttachmentIcon(file.name)}
															<div className="min-w-0">
																<Typography className="truncate" fontWeight={600}>
																	{file.name}
																</Typography>
																<Typography variant="caption" color="text.secondary">
																	{formatFileSize(file.size)}
																</Typography>
															</div>
														</button>
														<IconButton
															size="small"
															aria-label={`Remove ${file.name}`}
															onClick={() => handleRemoveAttachment(file.attachmentId)}
														>
															<CloseIcon fontSize="small" />
														</IconButton>
													</div>
												))}
												{removedAttachmentIds.length > 0 && (
													<Button
														size="small"
														variant="text"
														className="md:col-span-3"
														onClick={handleRestoreAttachments}
													>
														Restore removed files ({removedAttachmentIds.length})
													</Button>
												)}
											</div>
										)}
									</div>
									{isAdmin && (
										<Button
											className=""
											variant="outlined"
											onClick={handleSendToInstructor}
											startIcon={<SendIcon />}
											disabled={
												emailSendDisabled ||
												zipping ||
												downloadingAll ||
												sendingToInstructor ||
												emailSent
											}
											sx={{ borderStyle: "solid", borderRadius: "30px", py: 1 }}
										>
											{sendingToInstructor ? (
												<CircularProgress size={24} color="inherit" />
											) : (
												`Send Email (${visibleEmailAttachments.length} attachment${
													visibleEmailAttachments.length === 1 ? "" : "s"
												})`
											)}
										</Button>
									)}
								</div>
							</Box>
						)}
					</Box>
				</DialogContent>
			</div>

			<DialogActions>
				<Button color="primary" onClick={handleClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
}
